import { Head, Link, useForm } from "@inertiajs/react"
import { Plus, Trash2 } from "lucide-react"

import { humanize } from "@/components/products/fleet/format"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface NamedRef {
  id: number
  name: string
}

interface QualRow {
  id: number | null
  item_name: string
  due_on: string
  last_completed_on: string
  interval_years: string
  _destroy?: boolean
}

interface CarProps {
  id: number
  mark_number: string
  reporting_mark: string
  car_number: string
  fleet_code: string | null
  car_type: string | null
  status: string
  lessor_id: number | null
  rider_id: number | null
  facility_id: number | null
  stencil_spec: string | null
  year_built: number | null
  capacity_gallons: number | null
  gross_rail_load_lbs: number | null
  load_limit_lbs: number | null
  tare_weight_lbs: number | null
  allowable_miles: number | null
  notes: string | null
}

interface Props {
  car: CarProps | null
  qual_items: {
    id: number
    item_name: string
    due_on: string | null
    last_completed_on: string | null
    interval_years: number | null
  }[]
  form_options: {
    fleet_codes: string[]
    car_types: string[]
    statuses: string[]
    lessors: NamedRef[]
    facilities: NamedRef[]
    riders: {
      id: number
      lessor_id: number
      rider_number: string
      label: string
    }[]
    qual_item_names: string[]
  }
}

// Radix forbids an empty Select value, so "none" stands in for "not set" and
// is converted back to "" on submit.
const NONE = "none"

function str(value: string | number | null | undefined): string {
  return value == null ? "" : String(value)
}

export default function CarEdit({ car, qual_items, form_options }: Props) {
  const routes = useOrgRoutes()
  const isEdit = car != null

  const form = useForm<{
    reporting_mark: string
    car_number: string
    fleet_code: string
    car_type: string
    status: string
    lessor_id: string
    rider_id: string
    facility_id: string
    stencil_spec: string
    year_built: string
    capacity_gallons: string
    gross_rail_load_lbs: string
    load_limit_lbs: string
    tare_weight_lbs: string
    allowable_miles: string
    notes: string
    qual_items: QualRow[]
  }>({
    reporting_mark: str(car?.reporting_mark),
    car_number: str(car?.car_number),
    fleet_code: str(car?.fleet_code),
    car_type: str(car?.car_type),
    status: car?.status ?? "active",
    lessor_id: str(car?.lessor_id),
    rider_id: str(car?.rider_id),
    facility_id: str(car?.facility_id),
    stencil_spec: str(car?.stencil_spec),
    year_built: str(car?.year_built),
    capacity_gallons: str(car?.capacity_gallons),
    gross_rail_load_lbs: str(car?.gross_rail_load_lbs),
    load_limit_lbs: str(car?.load_limit_lbs),
    tare_weight_lbs: str(car?.tare_weight_lbs),
    allowable_miles: str(car?.allowable_miles),
    notes: str(car?.notes),
    qual_items: qual_items.map((item) => ({
      id: item.id,
      item_name: item.item_name,
      due_on: str(item.due_on),
      last_completed_on: str(item.last_completed_on),
      interval_years: str(item.interval_years),
    })),
  })

  const { data, setData, processing, errors } = form

  // Riders belong to a lessor, so offering all of them would let an operator
  // put a car on a rider its lessor never wrote.
  const availableRiders = data.lessor_id
    ? form_options.riders.filter(
        (rider) => String(rider.lessor_id) === data.lessor_id,
      )
    : form_options.riders

  function setQualRow(index: number, patch: Partial<QualRow>) {
    setData(
      "qual_items",
      data.qual_items.map((row, i) =>
        i === index ? { ...row, ...patch } : row,
      ),
    )
  }

  function addQualRow() {
    setData("qual_items", [
      ...data.qual_items,
      {
        id: null,
        item_name: "",
        due_on: "",
        last_completed_on: "",
        interval_years: "",
      },
    ])
  }

  // A persisted row is TOMBSTONED rather than dropped: the server needs to be
  // told to destroy it. A row that was never saved just leaves the array.
  function removeQualRow(index: number) {
    const row = data.qual_items[index]
    if (row.id == null) {
      setData(
        "qual_items",
        data.qual_items.filter((_, i) => i !== index),
      )
    } else {
      setQualRow(index, { _destroy: true })
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    form.transform(({ qual_items: rows, ...carFields }) => ({
      car: carFields,
      qual_items: rows.map((row) => ({
        ...row,
        id: row.id ?? "",
        _destroy: row._destroy ? "1" : "",
      })),
    }))

    if (isEdit) form.patch(routes.carPath(car.id))
    else form.post(routes.carsPath())
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Cars", href: routes.carsPath() },
    ...(isEdit
      ? [
          { title: car.mark_number, href: routes.carPath(car.id) },
          { title: "Edit", href: routes.editCarPath(car.id) },
        ]
      : [{ title: "New car", href: routes.newCarPath() }]),
  ]

  const visibleQualRows = data.qual_items
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => !row._destroy)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={isEdit ? `Edit ${car.mark_number}` : "New car"} />

      <div className="mx-auto w-full max-w-3xl p-4">
        <form onSubmit={submit} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Identity</CardTitle>
              <CardDescription>
                The reporting mark and number combine into the car&rsquo;s
                unique mark number.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="reporting_mark"
                label="Reporting mark"
                value={data.reporting_mark}
                onChange={(v) => setData("reporting_mark", v.toUpperCase())}
                error={errors.reporting_mark}
                placeholder="UTLX"
                required
              />
              <TextField
                id="car_number"
                label="Car number"
                value={data.car_number}
                onChange={(v) => setData("car_number", v)}
                error={errors.car_number}
                placeholder="640123"
                required
              />
              <SelectField
                id="fleet_code"
                label="Fleet"
                value={data.fleet_code}
                onChange={(v) => setData("fleet_code", v)}
                options={form_options.fleet_codes.map((c) => ({
                  value: c,
                  label: c,
                }))}
                noneLabel="No fleet"
              />
              <SelectField
                id="car_type"
                label="Car type"
                value={data.car_type}
                onChange={(v) => setData("car_type", v)}
                options={form_options.car_types.map((t) => ({
                  value: t,
                  label: humanize(t),
                }))}
                noneLabel="Not set"
              />
              <SelectField
                id="status"
                label="Status"
                value={data.status}
                onChange={(v) => setData("status", v)}
                options={form_options.statuses.map((s) => ({
                  value: s,
                  label: humanize(s),
                }))}
                allowNone={false}
              />
              <SelectField
                id="facility_id"
                label="Facility"
                value={data.facility_id}
                onChange={(v) => setData("facility_id", v)}
                options={form_options.facilities.map((f) => ({
                  value: String(f.id),
                  label: f.name,
                }))}
                noneLabel="Unassigned"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Lease</CardTitle>
              <CardDescription>
                The rider carries the lease end date the requal program is
                planned against.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <SelectField
                id="lessor_id"
                label="Lessor"
                value={data.lessor_id}
                onChange={(v) => {
                  setData((current) => ({
                    ...current,
                    lessor_id: v,
                    // A rider from the old lessor can't survive the switch.
                    rider_id: "",
                  }))
                }}
                options={form_options.lessors.map((l) => ({
                  value: String(l.id),
                  label: l.name,
                }))}
                noneLabel="No lessor"
              />
              <SelectField
                id="rider_id"
                label="Rider"
                value={data.rider_id}
                onChange={(v) => setData("rider_id", v)}
                options={availableRiders.map((r) => ({
                  value: String(r.id),
                  label: r.label,
                }))}
                noneLabel="No rider"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Specification</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="stencil_spec"
                label="Stencil spec"
                value={data.stencil_spec}
                onChange={(v) => setData("stencil_spec", v)}
                error={errors.stencil_spec}
                placeholder="DOT-117R100W"
              />
              <TextField
                id="year_built"
                label="Year built"
                value={data.year_built}
                onChange={(v) => setData("year_built", v)}
                error={errors.year_built}
                inputMode="numeric"
              />
              <TextField
                id="capacity_gallons"
                label="Capacity (gal)"
                value={data.capacity_gallons}
                onChange={(v) => setData("capacity_gallons", v)}
                error={errors.capacity_gallons}
                inputMode="numeric"
              />
              <TextField
                id="allowable_miles"
                label="Allowable miles"
                value={data.allowable_miles}
                onChange={(v) => setData("allowable_miles", v)}
                error={errors.allowable_miles}
                inputMode="numeric"
              />
              <TextField
                id="gross_rail_load_lbs"
                label="Gross rail load (lb)"
                value={data.gross_rail_load_lbs}
                onChange={(v) => setData("gross_rail_load_lbs", v)}
                error={errors.gross_rail_load_lbs}
                inputMode="numeric"
              />
              <TextField
                id="load_limit_lbs"
                label="Load limit (lb)"
                value={data.load_limit_lbs}
                onChange={(v) => setData("load_limit_lbs", v)}
                error={errors.load_limit_lbs}
                inputMode="numeric"
              />
              <TextField
                id="tare_weight_lbs"
                label="Tare weight (lb)"
                value={data.tare_weight_lbs}
                onChange={(v) => setData("tare_weight_lbs", v)}
                error={errors.tare_weight_lbs}
                inputMode="numeric"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Qualification map</CardTitle>
              <CardDescription>
                The earliest due date here becomes the car&rsquo;s next qual
                due, which drives the requal program and the lease-conflict
                flag.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {visibleQualRows.length === 0 && (
                <p className="text-muted-foreground rounded-md border border-dashed p-3 text-sm">
                  No qualification items yet. Add one so this car appears in the
                  requal program.
                </p>
              )}

              {visibleQualRows.map(({ row, index }) => (
                <div
                  key={row.id ?? `new-${index}`}
                  className="grid items-end gap-2 sm:grid-cols-[2fr_1fr_1fr_auto]"
                >
                  <div className="space-y-1">
                    <Label htmlFor={`qual-name-${index}`}>Item</Label>
                    <Input
                      id={`qual-name-${index}`}
                      list="qual-item-names"
                      value={row.item_name}
                      onChange={(e) =>
                        setQualRow(index, { item_name: e.target.value })
                      }
                      placeholder="Tank test"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`qual-due-${index}`}>Due</Label>
                    <Input
                      id={`qual-due-${index}`}
                      type="date"
                      value={row.due_on}
                      onChange={(e) =>
                        setQualRow(index, { due_on: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`qual-interval-${index}`}>
                      Interval (yr)
                    </Label>
                    <Input
                      id={`qual-interval-${index}`}
                      inputMode="numeric"
                      value={row.interval_years}
                      onChange={(e) =>
                        setQualRow(index, { interval_years: e.target.value })
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeQualRow(index)}
                    aria-label={`Remove ${row.item_name || "qualification item"}`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}

              <datalist id="qual-item-names">
                {form_options.qual_item_names.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addQualRow}
              >
                <Plus className="mr-1 size-4" /> Add qualification item
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                id="notes"
                value={data.notes}
                onChange={(e) => setData("notes", e.target.value)}
                rows={4}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button variant="outline" asChild>
              <Link href={isEdit ? routes.carPath(car.id) : routes.carsPath()}>
                Cancel
              </Link>
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? "Saving…" : isEdit ? "Save changes" : "Add car"}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}

function TextField({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  required,
  inputMode,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  // Rails hands back one message or several per attribute, so accept both
  // rather than dropping the extras on the floor.
  error?: string | string[]
  placeholder?: string
  required?: boolean
  inputMode?: "numeric"
}) {
  const message = Array.isArray(error) ? error.join(", ") : error

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        inputMode={inputMode}
      />
      {message && <p className="text-destructive text-sm">{message}</p>}
    </div>
  )
}

function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  noneLabel,
  allowNone = true,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  noneLabel?: string
  allowNone?: boolean
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value === "" ? NONE : value}
        onValueChange={(next) => onChange(next === NONE ? "" : next)}
      >
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {allowNone && (
            <SelectItem value={NONE}>{noneLabel ?? "Not set"}</SelectItem>
          )}
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
